<script setup>
import { ref, computed, watch } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { useFirmwaresStore } from '@/stores/firmwares'
import { useCarsStore } from '@/stores/cars'
import { useCarsGroupsStore } from '@/stores/cars-groups'
import Modal from '@/components/UTAModal.vue'
import UTAListSelector from '@/components/UTAListSelector.vue'

const props = defineProps({
    fwName: {
        type: String,
        required: true
    }
})

function addItem(item) {
    selectedItems.value.push(item)
}

function deleteItem(item) {
    selectedItems.value = selectedItems.value.filter(selectedItem => selectedItem.uuid !== item.uuid)
}

function submitFirmwareForm() {

    formErrors.value = []
    formInvalids.value = {}

    let data = {
        name: encodeURI(UTAFirmwareFile.value)
    }

    if(UTAFirmwareMethod.value) {
        let methodKey = ''
        if(UTAFirmwareMethod.value == "cars") {
            methodKey = 'uuid_car_array'
        } else if(UTAFirmwareMethod.value == "groups") {
            methodKey = 'uuid_cgroup_array'
        }
        selectedItems.value.map(item => {
            data[methodKey] ? 
                data[methodKey].push(item.uuid) :
                data[methodKey] = [item.uuid]
        })
    } else {
        formErrors.value.push('UTAFirmwareMethod')
        formInvalids.value.UTAFirmwareMethod = true
    }

    if(UTAFirmwareDevice.value) {
        data.device = UTAFirmwareDevice.value
    } else {
        formErrors.value.push('UTAFirmwareDevice')
        formInvalids.value.UTAFirmwareDevice = true
    }

    if(UTAFirmwareModule.value) {
        data.module_type = UTAFirmwareModule.value
    } else {
        formErrors.value.push('UTAFirmwareModule')
        formInvalids.value.UTAFirmwareModule = true
    }

    if(UTAFirmwareVersion.value) {
        data.software_version = UTAFirmwareVersion.value
    } else {
        formErrors.value.push('UTAFirmwareVersion')
        formInvalids.value.UTAFirmwareVersion = true
    }

    if(formErrors.value.length == 0) {
        firmwaresStore.updateFirmwares(data)
    }
}

const translaterStore           = useTranslaterStore()
const firmwaresStore            = useFirmwaresStore()
const carsStore                 = useCarsStore()
const carsGroupsStore           = useCarsGroupsStore()

const getTranslate              = computed(() => translaterStore.getTranslate)
const firmwareProcess           = computed(() => firmwaresStore.firmwareProcess)
const firmwareUpdateMsg         = computed(() => firmwaresStore.firmwareUpdateMsg)
const firmwareDeleteProcess     = computed(() => firmwaresStore.firmwareDeleteProcess)
const firmwareDeleteMsg         = computed(() => firmwaresStore.firmwareDeleteMsg)

const formInvalids          = ref({})
const formErrors            = ref([])
const selectedItems         = ref([])
const showFirmwaresSelector = ref(false)
const showFirmwareDelete    = ref(false)

const UTAFirmwareMethod     = ref('')
const UTAFirmwareDevice     = ref()
const UTAFirmwareFile       = ref()
const UTAFirmwareModule     = ref()
const UTAFirmwareVersion    = ref()

firmwaresStore.readFirmware(props.fwName).then(() => {

    UTAFirmwareMethod.value     = ''
    UTAFirmwareDevice.value     = firmwaresStore.firmware.device
    UTAFirmwareFile.value       = firmwaresStore.firmware.name
    UTAFirmwareModule.value     = firmwaresStore.firmware.moduleType
    UTAFirmwareVersion.value    = firmwaresStore.firmware.softwareVersion

    if(firmwaresStore.firmware.uuidCarArray?.length) {

        UTAFirmwareMethod.value = 'cars'

        carsStore.readCars({ 
            'c.limit': 1000, 
            uuid_car_array: firmwaresStore.firmware.uuidCarArray 
        }).then(cars => selectedItems.value = cars.array)

    } else if(firmwaresStore.firmware.uuidCgroupArray?.length) {

        UTAFirmwareMethod.value = 'groups'

        selectedItems.value = carsGroupsStore.readCarsGroups({ 
            'c.limit': 1000, 
            uuid_array: firmwaresStore.firmware.uuidCgroupArray
        }).then(carsGroups => selectedItems.value = carsGroups.array)

    } else {

        UTAFirmwareMethod.value = 'all'
    }
})

const sourceList = computed(() => {

    if(UTAFirmwareMethod.value == 'cars') return carsStore.readCars
    if(UTAFirmwareMethod.value == 'groups') return carsGroupsStore.readCarsGroups

    return false
})

const sourceListNames = computed(() => {

    if(UTAFirmwareMethod.value == 'cars') return ['data.imei', 'data.vin']
    if(UTAFirmwareMethod.value == 'groups') return ['name', 'description']

    return []
})

const sourceListLabels = computed(() => {

    if(UTAFirmwareMethod.value == 'cars') return ['CARS_VIN', 'CARS_IMEI']
    if(UTAFirmwareMethod.value == 'groups') return ['CARS_GROUPS_NAME', 'CARS_GROUPS_DESCR']

    return []
})

watch(UTAFirmwareMethod, () => {
    selectedItems.value = []
})

watch(firmwareDeleteMsg, msg => {
    if(msg.success) {
        showFirmwareDelete.value = false
    }
})

</script>

<template>
    <div class="my-4 uta-firmware">

        <div class="container-fluid">

            <header class="uta-header" role="heading" aria-level="1">
                <h1 class="uta-header__title">{{ firmwaresStore.firmware.name || getTranslate.MENU_FIRMWARE }}</h1>
                <div class="spinner-border uta-header__spinner" role="status" v-if="firmwareProcess">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </header>

            <div 
                v-if="'success' in firmwareUpdateMsg"
                class="my-4 uta-alert" 
                :class="{
                    'uta-alert--success': firmwareUpdateMsg.success, 
                    'uta-alert--danger': !firmwareUpdateMsg.success
                }"
            >
                <div class="uta-alert__icon">
                    <span class="material-icons">
                        {{ firmwareUpdateMsg.success ? 'check_circle' : 'error_outline' }}
                    </span>
                </div>
                <div class="uta-alert__text">
                    {{ getTranslate[firmwareUpdateMsg.text] || firmwareUpdateMsg.text }}
                </div>
                <button type="button" class="uta-alert__close" @click="firmwaresStore.setFirmwareUpdateMsg({})">
                    <span class="material-icons">
                        close
                    </span>
                </button>
            </div>

            <form class="uta-firmware__form" @submit.prevent="submitFirmwareForm()">
                <div class="row">
                    <div class="col-12 col-md-4 my-2">
                        <label for="UTAFirmwareMethod">{{ getTranslate.FIRMWARES_FORM_METHOD }}:</label>
                        <select 
                            id="UTAFirmwareMethod" 
                            v-model="UTAFirmwareMethod"
                            class="uta-input uta-input--sm uta-input--block"
                            :class="{ 'uta-input--invalid' : formInvalids.UTAFirmwareMethod }"
                            @change="delete formInvalids.UTAFirmwareMethod"
                        >
                            <option value="cars">{{ getTranslate.FIRMWARES_FORM_METHOD_CARS }}</option>
                            <option value="groups">{{ getTranslate.FIRMWARES_FORM_METHOD_GROUPS }}</option>
                            <option value="all">{{ getTranslate.FIRMWARES_FORM_METHOD_ALL }}</option>
                        </select>
                    </div>
                    <div class="col-12 col-md-4 my-2">
                        <label for="UTAFirmwareDevice">{{ getTranslate.FIRMWARES_FORM_DEVICE }}:</label>
                        <select 
                            id="UTAFirmwareDevice" 
                            v-model="UTAFirmwareDevice" 
                            class="uta-input uta-input--sm uta-input--block"
                            :class="{ 'uta-input--invalid': formInvalids.UTAFirmwareDevice }"
                            @change="delete formInvalids.UTAFirmwareDevice"
                        >
                            <option value="SDM_V1_2">{{ getTranslate.FIRMWARES_FORM_DEVICE_12 }}</option>
                            <option value="SDM_V3_0" disabled>{{ getTranslate.FIRMWARES_FORM_DEVICE_30 }}</option>
                            <option value="NAMI_INTELCOM_V2_1_7">{{ getTranslate.FIRMWARES_FORM_DEVICE_IC_217 }}</option>
                        </select>
                    </div>
                    <div class="col-12 col-md-4 my-2">
                        <label for="UTAFirmwareFile">{{ getTranslate.FIRMWARES_FORM_FILE }}:</label>
                        <input 
                            id="UTAFirmwareFile" 
                            readonly
                            v-model="UTAFirmwareFile"
                            type="text" 
                            class="uta-input uta-input--sm uta-input--block"
                        >
                    </div>
                    <div class="col-12 col-md-4 my-2">
                        <label for="UTAFirmwareModule">{{ getTranslate.FIRMWARES_FORM_MODULE }}:</label>
                        <input 
                            id="UTAFirmwareModule" 
                            name="module_type"
                            v-model="UTAFirmwareModule" 
                            type="text" 
                            class="uta-input uta-input--sm uta-input--block"
                            :class="{ 'uta-input--invalid': formInvalids.UTAFirmwareModule }"
                            @change="delete formInvalids.UTAFirmwareModule"
                        >
                    </div>
                    <div class="col-12 col-md-4 my-2">
                        <label for="UTAFirmwareVersion">{{ getTranslate.FIRMWARES_FORM_VERSION }}:</label>
                        <input 
                            id="UTAFirmwareVersion" 
                            name="software_version"
                            v-model="UTAFirmwareVersion" 
                            type="text" 
                            class="uta-input uta-input--sm uta-input--block"
                            :class="{ 'uta-input--invalid': formInvalids.UTAFirmwareVersion }"
                            @change="delete formInvalids.UTAFirmwareVersion"
                        >
                    </div>
                    <div class="col-12 col-md-4 my-2 d-flex align-items-end">
                        <button 
                            type="submit" 
                            class="uta-btn uta-btn--main uta-btn--block uta-btn--sm"
                        >
                            {{ getTranslate.BTN_UPDATE }}
                        </button>
                    </div>
                </div>
                <div 
                    class="uta-firmwares__destination" 
                    v-if="
                        UTAFirmwareMethod == 'cars' || 
                        UTAFirmwareMethod == 'groups'
                    "
                >
                    <template v-if="selectedItems.length">
                        <div class="uta-firmwares__destination-list">
                            <template v-if="UTAFirmwareMethod == 'cars'">
                                <div 
                                    class="uta-firmwares__destination-list-item"
                                    v-for="selectedItem in selectedItems"
                                    :key="selectedItem.uuid"
                                >
                                    <router-link 
                                        :to="'/cars/' + selectedItem.uuid" 
                                        target="_blank" 
                                        class="uta-firmwares__destination-list-item-link"
                                    >
                                        {{ selectedItem.data.imei }} 
                                    </router-link>
                                    <span 
                                        @click="deleteItem(selectedItem)"
                                        class="material-symbols-outlined uta-firmwares__destination-list-item-delete"
                                    >
                                        close
                                    </span>
                                </div>
                            </template>
                            <template v-if="UTAFirmwareMethod == 'groups'">
                                <div 
                                    class="uta-firmwares__destination-list-item"
                                    v-for="selectedItem in selectedItems"
                                    :key="selectedItem.uuid"
                                >
                                    <router-link 
                                        :to="'/cars-groups/' + selectedItem.uuid" 
                                        target="_blank" 
                                        class="uta-firmwares__destination-list-item-link"
                                    >
                                        {{ selectedItem.name }} 
                                    </router-link>
                                    <span 
                                        @click="deleteItem(selectedItem)"
                                        class="material-symbols-outlined uta-firmwares__destination-list-item-delete"
                                    >
                                        close
                                    </span>
                                </div>
                            </template>
                        </div>
                    </template>
                    <template v-else>
                        <div class="uta-firmwares__destination-descr">
                            {{ getTranslate.FIRMWARES_FORM_SELECT_CARS }}
                        </div>
                    </template>
                    <a 
                        href="#" 
                        class="uta-btn uta-btn--inline uta-firmwares__destination-select"
                        @click="showFirmwaresSelector = true"
                    >
                        {{ getTranslate.FIRMWARES_SELECTOR_LINK }}
                    </a>
                </div>
                <div v-if="UTAFirmwareMethod == 'all'">
                    <div class="uta-firmwares__destination-descr">
                        {{ getTranslate.FIRMWARES_FORM_SELECT_WARNING }}
                    </div>
                </div>
            </form>

            <transition name="bounce">
                <Modal 
                    v-if="showFirmwaresSelector"
                    :header="getTranslate.FIRMWARES_SELECTOR"
                    :size="'lg'"
                    @closeModal="showFirmwaresSelector = false"
                >
                    <template v-slot:body>
                        <UTAListSelector 
                            @addItem="addItem"
                            @deleteItem="deleteItem"
                            :sourceListSelected="selectedItems"
                            :sourceList="sourceList"
                            :sourceListLabels="sourceListLabels"
                            :sourceListNames="sourceListNames"
                            @closeModal="showFirmwaresSelector = false" 
                        />
                    </template>
                </Modal>
            </transition>

            <transition>
                <Modal
                    v-if="showFirmwareDelete"
                    :header="getTranslate.FIRMWARES_DELETE"
                    size="md"
                    @closeModal="showFirmwareDelete = false"
                >
                    <template v-slot:body>
                        <form action="" @submit.prevent="firmwaresStore.deleteFirmwares(deleteFirmwareName)">
                            {{ getTranslate.FIRMWARES_DELETE_DESCR }}
                            <div class="mt-5 uta-btns">
                                <button type="button" class="uta-btn" @click="showFirmwareDelete = false">{{ getTranslate.BTN_CANCEL }}</button>
                                <button type="submit" class="uta-btn uta-btn--main" :disabled="firmwareDeleteProcess">{{ getTranslate.BTN_DELETE }}</button>
                            </div>
                        </form>
                    </template>
                </Modal>
            </transition>
        </div>
    </div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/firmwares.scss';
</style>